import React, { useEffect, Suspense } from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useStore } from "@/stores";
import { ProtectedRoute } from "@/components/common/ProtectedRoute";
import { ROUTES } from "@/utils/constants";
import { PageLoading } from "@/components/common/LoadingState";
import type { Permission } from "@/utils/permissions";

// Lazy load des pages
const LoginPage = React.lazy(() =>
  import("@/pages").then((m) => ({ default: m.LoginPage }))
);
const DashboardPage = React.lazy(() =>
  import("@/pages").then((m) => ({ default: m.DashboardPage }))
  );
const HistoryPage = React.lazy(() =>
  import("@/pages").then((m) => ({ default: m.HistoryPage }))
);
const NotificationsPage = React.lazy(() =>
  import("@/pages").then((m) => ({ default: m.NotificationsPage }))
);
const RTGManagementPage = React.lazy(() =>
  import("@/pages").then((m) => ({ default: m.RTGManagementPage }))
);
const RTGCRUDPage = React.lazy(() =>
  import("@/pages").then((m) => ({ default: m.RTGCRUDPage }))
);
const RTGDetailPage = React.lazy(() =>
  import("@/pages").then((m) => ({ default: m.RTGDetailPage }))
);
const SettingsPage = React.lazy(() =>
  import("@/pages").then((m) => ({ default: m.SettingsPage }))
);
const SubBlockPriorityPage = React.lazy(() =>
  import("@/pages").then((m) => ({ default: m.SubBlockPriorityPage }))
);
const UsersManagementPage = React.lazy(() =>
  import("@/pages").then((m) => ({ default: m.UsersManagementPage }))
);
const TeamsManagementPage = React.lazy(() =>
  import("@/pages").then((m) => ({ default: m.TeamsManagementPage }))
);
const TeamDetailPage = React.lazy(() =>
  import("@/pages").then((m) => ({ default: m.TeamDetailPage }))
);
const RepartitionPage = React.lazy(() =>
  import("@/pages").then((m) => ({ default: m.RepartitionPage }))
);
const RepartitionDetailPage = React.lazy(() =>
  import("@/pages").then((m) => ({ default: m.RepartitionDetailPage }))
);
interface ProtectedRouteConfig {
  path: string;
  element: React.ReactNode;
  requirePermission?: Permission;
}

// Config des routes protégées + permissions front
const protectedRoutes: ProtectedRouteConfig[] = [
  // Accessible par tous les rôles web
  { path: "/dashboard", element: <DashboardPage /> },
  { path: "/history", element: <HistoryPage /> },
  { path: "/notifications", element: <NotificationsPage /> },
  { path: "/settings", element: <SettingsPage /> },

  // Référentiels RTG / blocs (admin + control_room)
  {
    path: "/rtg-management",
    element: <RTGManagementPage />,
    requirePermission: "rtg:crud",
  },
  {
    path: "/rtg-crud",
    element: <RTGCRUDPage />,
    requirePermission: "rtg:crud",
  },
  {
    path: "/rtg-crud/:id",
    element: <RTGDetailPage />,
    requirePermission: "rtg:crud",
  },
  {
    path: "/sub-block-priority",
    element: <SubBlockPriorityPage />,
    requirePermission: "blocks:crud",
  },
  {
    path: "/users",
    element: <UsersManagementPage />,
    // Admin (users:any:crud) + chef_section (users:operators:crud)
    requirePermission: "users:operators:crud",
  },
  {
    path: "/teams",
    element: <TeamsManagementPage />,
    requirePermission: "users:any:crud",
  },
  {
    path: "/teams/:id",
    element: <TeamDetailPage />,
    requirePermission: "users:any:crud",
  },
  { path: "/repartitions", element: <RepartitionPage /> },
  { path: "/repartitions/:id", element: <RepartitionDetailPage /> },
];

function App() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { settings, setUser } = useStore();

  useEffect(() => {
    if (settings.darkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [settings.darkMode]);

  // Écouter auth:logout (refresh token échoué) → déconnexion et redirection
  useEffect(() => {
    const handleLogout = () => {
      setUser(null);
      navigate(ROUTES.AUTH.LOGIN);
    };
    window.addEventListener("auth:logout", handleLogout);
    return () => window.removeEventListener("auth:logout", handleLogout);
  }, [navigate, setUser]);

  // Écouter auth:refreshed → mettre à jour l'utilisateur et invalider le cache
  useEffect(() => {
    const handleRefreshed = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.user) setUser(detail.user);
      queryClient.invalidateQueries({ queryKey: ["currentUser"] });
    };
    window.addEventListener("auth:refreshed", handleRefreshed);
    return () => window.removeEventListener("auth:refreshed", handleRefreshed);
  }, [setUser, queryClient]);

  return (
    <div className="min-h-screen bg-background">
      <Suspense fallback={<PageLoading message="Chargement de l'application..." />}>
        <Routes>
          {/* Routes publiques */}
          <Route path="/auth/login" element={<LoginPage />} />

          {/* Routes protégées */}
          {protectedRoutes.map(({ path, element, requirePermission }) => (
            <Route
              key={path}
              path={path}
              element={
                <ProtectedRoute requirePermission={requirePermission}>
                  {element}
                </ProtectedRoute>
              }
            />
          ))}

          {/* Redirection vers la page de dashboard */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Suspense>
    </div>
  );
}

export default App;